import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import { SubscribePage, SubscribeSuccess } from "./pages/Subscribe";
import Feed from "./pages/Feed";
import Profile from "./pages/Profile";
import Marketplace from "./pages/Marketplace";
import Discover from "./pages/Discover";
import Leaderboard from "./pages/Leaderboard";
import BookingSuccess from "./pages/BookingSuccess";
import BookingDetail from "./pages/BookingDetail";
import Messages from "./pages/Messages";
import CallRoom from "./pages/CallRoom";
import GoLive from "./pages/GoLive";
import Wallet from "./pages/Wallet";
import Admin from "./pages/Admin";
import Ads from "./pages/Ads";
import Referrals from "./pages/Referrals";
import Notifications from "./pages/Notifications";
import Friends from "./pages/Friends";
import Cashout from "./pages/Cashout";
import Saved from "./pages/Saved";
import Unsubscribe from "./pages/Unsubscribe";
import Elders from "./pages/Elders";
import Terms from "./pages/Terms";
import RiskDisclosures from "./pages/RiskDisclosures";
import Privacy from "./pages/Privacy";
import CommunityGuidelines from "./pages/CommunityGuidelines";
import AccountDeletion from "./pages/AccountDeletion";
import Clips from "./pages/Clips";
import CreateClip from "./pages/CreateClip";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { LivestreamList, LivestreamDetail } from "./pages/Livestreams";
import { CommunityList, CommunityDetail } from "./pages/Communities";
import Oracle from "./pages/Oracle";
import TempleHome from "./pages/Temple";
import TempleVerify from "./pages/TempleVerify";
import OracleShare from "./pages/OracleShare";
import ElderProfile from "./pages/ElderProfile";
import FounderShareRedirect from "./pages/FounderShareRedirect";
import Welcome from "./pages/Welcome";
import PractitionerWelcome from "./pages/PractitionerWelcome";
import Ambassadors from "./pages/Ambassadors";
import AmbassadorApply from "./pages/AmbassadorApply";
import AmbassadorDashboard from "./pages/AmbassadorDashboard";
import AmbassadorKit from "./pages/AmbassadorKit";
import AmbassadorLeaderboard from "./pages/AmbassadorLeaderboard";
import AmbassadorSignup from "./pages/AmbassadorSignup";
import AmbassadorBackOffice from "./pages/AmbassadorBackOffice";
import FAQ from "./pages/FAQ";
import NicheLanding from "./pages/NicheLanding";
import ClientLanding from "./pages/ClientLanding";
import ReferralRedirect from "./pages/ReferralRedirect";
import Pricing from "./pages/Pricing";
import ProHub from "./pages/ProHub";
import ProClients from "./pages/ProClients";
import ProInvoices from "./pages/ProInvoices";
import ProCourses from "./pages/ProCourses";
import ProCoupons from "./pages/ProCoupons";
import ProAnalytics from "./pages/ProAnalytics";
import { CoursesList, CourseDetail } from "./pages/Courses";
import { OrgsList, OrgDashboard } from "./pages/Orgs";
import OrgPublicPage from "./pages/OrgPublicPage";
import AdminRewards from "./pages/AdminRewards";
import UtmTracker from "./components/UtmTracker";
import GaTracker from "./components/GaTracker";
import MobileBootstrap from "./components/MobileBootstrap";
import PWANavBar from "./components/PWANavBar";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-serif text-3xl">Loading...</div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UtmTracker />
        <GaTracker />
        <MobileBootstrap />
        <PWANavBar />
        <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#1E1638", border: "1px solid rgba(212,181,140,0.32)", color: "#F4EEDA" } }} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/subscribe" element={<Protected><SubscribePage /></Protected>} />
          <Route path="/subscribe/success" element={<Protected><SubscribeSuccess /></Protected>} />
          <Route path="/feed" element={<Protected><Feed /></Protected>} />
          <Route path="/welcome" element={<Protected><Welcome /></Protected>} />
          <Route path="/welcome-practitioner" element={<Protected><PractitionerWelcome /></Protected>} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/marketplace" element={<Protected><Marketplace /></Protected>} />
          <Route path="/discover" element={<Protected><Discover /></Protected>} />
          <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
          <Route path="/profile/:uid" element={<Protected><Profile /></Protected>} />
          <Route path="/booking/success" element={<Protected><BookingSuccess /></Protected>} />
          <Route path="/bookings/:bid" element={<Protected><BookingDetail /></Protected>} />
          <Route path="/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/messages/:uid" element={<Protected><Messages /></Protected>} />
          <Route path="/call/:uid" element={<Protected><CallRoom /></Protected>} />
          <Route path="/golive" element={<Protected><GoLive /></Protected>} />
          <Route path="/live/:uid" element={<Protected><GoLive /></Protected>} />
          <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
          <Route path="/ads" element={<Protected><Ads /></Protected>} />
          <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/friends" element={<Protected><Friends /></Protected>} />
          <Route path="/cashout" element={<Protected><Cashout /></Protected>} />
          <Route path="/saved" element={<Protected><Saved /></Protected>} />
          <Route path="/profile/me/temple-verify" element={<Protected><TempleVerify /></Protected>} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/elders" element={<Elders />} />
          <Route path="/elders/:uid" element={<ElderProfile />} />
          <Route path="/share/founder/:uid" element={<FounderShareRedirect />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/risk-disclosures" element={<RiskDisclosures />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/community-guidelines" element={<CommunityGuidelines />} />
          <Route path="/account-deletion" element={<AccountDeletion />} />
          <Route path="/clips" element={<Protected><Clips /></Protected>} />
          <Route path="/clips/new" element={<Protected><CreateClip /></Protected>} />
          <Route path="/livestreams" element={<Protected><LivestreamList /></Protected>} />
          <Route path="/livestreams/:lid" element={<Protected><LivestreamDetail /></Protected>} />
          <Route path="/communities" element={<Protected><CommunityList /></Protected>} />
          <Route path="/communities/:cid" element={<Protected><CommunityDetail /></Protected>} />
          <Route path="/oracle" element={<Protected><Oracle /></Protected>} />
          <Route path="/oracle/share/:token" element={<OracleShare />} />
          <Route path="/temples" element={<Protected><TempleHome /></Protected>} />
          <Route path="/temple/:slug" element={<Protected><TempleHome /></Protected>} />
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          {/* ─── Ambassador Program ─── */}
          <Route path="/ambassadors" element={<Ambassadors />} />
          <Route path="/ambassadors/apply" element={<AmbassadorApply />} />
          <Route path="/ambassadors/kit" element={<AmbassadorKit />} />
          <Route path="/ambassadors/leaderboard" element={<AmbassadorLeaderboard />} />
          <Route path="/ambassador/dashboard" element={<Protected><AmbassadorDashboard /></Protected>} />
          <Route path="/ambassador/signup" element={<AmbassadorSignup />} />
          <Route path="/ambassador" element={<Protected><AmbassadorBackOffice /></Protected>} />
          <Route path="/ambassador/back-office" element={<Protected><AmbassadorBackOffice /></Protected>} />
          {/* ─── Programmatic niche landing pages (SEO) ─── */}
          <Route path="/for/clients" element={<ClientLanding />} />
          <Route path="/for/:slug" element={<NicheLanding />} />
          {/* Short referral links: /r/{code} redirects to Landing (or a
              named target via ?to=elders|ambassadors|clients|<niche>)
              while preserving the ref for UtmTracker to persist. */}
          <Route path="/r/:code" element={<ReferralRedirect />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pro" element={<Protected><ProHub /></Protected>} />
          <Route path="/pro/clients" element={<Protected><ProClients /></Protected>} />
          <Route path="/pro/invoices" element={<Protected><ProInvoices /></Protected>} />
          <Route path="/pro/courses" element={<Protected><ProCourses /></Protected>} />
          <Route path="/pro/coupons" element={<Protected><ProCoupons /></Protected>} />
          <Route path="/pro/analytics" element={<Protected><ProAnalytics /></Protected>} />
          <Route path="/courses" element={<CoursesList />} />
          <Route path="/courses/:cid" element={<Protected><CourseDetail /></Protected>} />
          <Route path="/orgs" element={<Protected><OrgsList /></Protected>} />
          <Route path="/orgs/:oid" element={<Protected><OrgDashboard /></Protected>} />
          <Route path="/o/:slug" element={<OrgPublicPage />} />
          <Route path="/admin/rewards" element={<Protected><AdminRewards /></Protected>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
